import type {
  HygieneCategory,
  HygieneCheck,
  HygieneCompleted,
  KnownHygieneCheckStatus
} from '../types/hygiene';
import { humanizeSlug } from './format';

/**
 * Investor-voice presentation for canonical hygiene data (product-marketing
 * skill). Backend strings are precise but engineering-voiced; every string a
 * user reads on the health surfaces is produced here instead. Raw backend
 * text stays available in the methodology dialog's for-the-record section.
 */

const CHECK_STATUS_LABEL: Record<KnownHygieneCheckStatus, string> = {
  met: 'Looks good',
  partially_met: 'Partly there',
  not_met: 'Needs attention',
  not_evaluable: 'Waiting on data',
  not_applicable: 'Not applicable'
};

/**
 * A check status in the reader's words. The status vocabulary can grow, so
 * a value this build has never seen reads as its own words rather than
 * borrowing the meaning of a status it is not.
 */
export function checkStatusLabel(status: string): string {
  return Object.hasOwn(CHECK_STATUS_LABEL, status)
    ? CHECK_STATUS_LABEL[status as KnownHygieneCheckStatus]
    : humanizeSlug(status);
}

interface CategoryVoice {
  title: string;
  /** One sentence: what this area tells the reader about their money. */
  tagline: string;
}

/**
 * v2 practice healths (the institution's three questions). Custody voices
 * stay shelved for the future ops surface; reconciliation/monitoring became
 * confidence signals (their per-check voices below still apply).
 */
const CATEGORY_VOICE: Record<string, CategoryVoice> = {
  concentration_diversification: {
    title: 'Risk health',
    tagline: 'Whether too much depends on any one asset, protocol, or chain.'
  },
  liquidity_resilience: {
    title: 'Liquidity health',
    tagline: 'How fast you could turn your portfolio back into cash.'
  },
  risk_evidence: {
    title: 'Asset health',
    tagline: 'Whether what you hold is vetted, current, and watched.'
  },
  custody_access: {
    title: 'Wallet control',
    tagline: 'Who can move money from this wallet, and is that reviewable.'
  },
  // v1.1 transition window only: these two left the score in v2 (they are
  // confidence signals now) but stored v1.1 assessments still serve them
  // as categories until each wallet recomputes.
  reconciliation_discipline: {
    title: 'Records that add up',
    tagline:
      "Your recorded holdings and flows match what's actually on the blockchain."
  },
  monitoring_data_quality: {
    title: 'Data freshness',
    tagline: 'The data behind this score is recent and healthy.'
  }
};

export function categoryTitle(category: HygieneCategory): string {
  return CATEGORY_VOICE[category.id]?.title ?? category.label;
}

export function categoryTagline(category: HygieneCategory): string {
  return CATEGORY_VOICE[category.id]?.tagline ?? category.description;
}

interface CheckVoice {
  /** Compact name that fits a tile meter row. */
  short?: string;
  /** Plain replacement for the definition's title. */
  title: string;
  /** What this check tells the reader — one spoken-aloud sentence. */
  explains: string;
}

/**
 * Keyed by canonical check id (hygiene definition v1). Unknown ids — future
 * waves — fall back to the backend title/description, which are user-safe
 * but stiffer.
 */
const CHECK_VOICE: Record<string, CheckVoice> = {
  authenticated_scope: {
    short: 'Ownership',
    title: 'Wallet ownership verified',
    explains:
      "You're signed in as this wallet's owner on the network being assessed."
  },
  ownership_authority_evidence: {
    short: 'Control proof',
    title: 'Proof of wallet control',
    explains: 'There is current proof of who controls this wallet on record.'
  },
  signing_practice: {
    short: 'Signing setup',
    title: 'Signing setup fits the account',
    explains: 'How transactions get signed matches the kind of account you use.'
  },
  safe_signer_threshold: {
    short: 'Safe approvals',
    title: 'Safe approval setup',
    explains:
      'For Safe accounts: enough separate owners must approve each transaction.'
  },
  unresolved_transaction_review: {
    short: 'Pending reviews',
    title: 'Pending transactions reviewed',
    explains:
      'Waiting transactions get looked at instead of piling up unreviewed.'
  },
  largest_position: {
    short: 'Biggest position',
    title: 'Biggest single investment',
    explains:
      'How much of your portfolio sits in your single biggest investment.'
  },
  largest_underlying_asset: {
    short: 'Biggest asset',
    title: 'Biggest single asset',
    explains:
      'How much of your portfolio depends on one asset, looking inside your vaults.'
  },
  protocol_chain_concentration: {
    short: 'Protocol spread',
    title: 'Spread across protocols and chains',
    explains:
      'Whether too much of your portfolio depends on one protocol or one chain.'
  },
  stablecoin_issuer_concentration: {
    short: 'Issuer spread',
    title: 'Spread across stablecoin issuers',
    explains: 'Whether your stablecoins lean too hard on a single issuer.'
  },
  t1_liquid_share: {
    short: 'Cash in a day',
    title: 'Money you can reach in a day',
    explains:
      'The share of your portfolio you could turn back into cash within one day.'
  },
  t7_exit_coverage: {
    short: 'Cash in a week',
    title: 'Money you can reach in a week',
    explains:
      'The share of your portfolio you could turn back into cash within seven days.'
  },
  verified_route_coverage: {
    short: 'Verified exits',
    title: 'Verified exits',
    explains:
      'How much of your portfolio has a checked, working path back to cash.'
  },
  lock_queue_concentration: {
    short: 'Locked money',
    title: 'Locked or queued money',
    explains:
      'How much of your portfolio sits behind locks, queues, or withdrawal windows.'
  },
  risk_assessment_coverage: {
    short: 'Risk ratings',
    title: 'Risk ratings in place',
    explains:
      'How much of your portfolio has a current risk assessment behind it.'
  },
  high_risk_exposure_boundary: {
    short: 'High risk share',
    title: 'High risk share kept in bounds',
    explains: 'How much of your portfolio sits in assets rated high risk.'
  },
  risk_source_provenance: {
    short: 'Named sources',
    title: 'Risk data has named sources',
    explains: 'Risk ratings come from named sources you could check yourself.'
  },
  unresolved_critical_findings: {
    short: 'Critical flags',
    title: 'No open critical risk flags',
    explains: 'Serious risk flags get resolved instead of staying open.'
  },
  normalized_valuation_coverage: {
    short: 'Consistent pricing',
    title: 'Holdings priced consistently',
    explains: 'How much of your portfolio is priced one consistent way.'
  },
  canonical_holdings_reconciliation: {
    short: 'Chain match',
    title: 'Records match the chain',
    explains:
      'Your recorded holdings match what actually sits on the blockchain.'
  },
  position_set_completeness: {
    short: 'Complete books',
    title: 'Nothing missing from the books',
    explains: 'Every position you hold shows up in the records.'
  },
  cashflow_value_consistency: {
    short: 'Flows add up',
    title: 'Deposits and value add up',
    explains:
      'Your deposits, withdrawals, and current value tell one consistent story.'
  },
  price_allocation_freshness: {
    short: 'Fresh prices',
    title: 'Prices are fresh',
    explains:
      'Prices, exchange rates, and vault breakdowns are recent, not stale.'
  },
  source_health_coverage: {
    short: 'Source health',
    title: 'Data sources are healthy',
    explains: 'The data feeds this assessment relies on are up and reporting.'
  },
  warning_age_review: {
    short: 'Warning handling',
    title: 'Warnings get handled',
    explains: 'Data warnings get reviewed quickly instead of aging out.'
  },
  stale_contradictory_evidence: {
    short: 'No stale data',
    title: 'No stale or conflicting data',
    explains: 'The assessment is not leaning on old or contradictory inputs.'
  }
};

export function checkTitle(check: HygieneCheck): string {
  return CHECK_VOICE[check.id]?.title ?? check.title;
}

/** Compact name sized for a tile meter row; falls back to the full title. */
export function checkShortTitle(check: HygieneCheck): string {
  return CHECK_VOICE[check.id]?.short ?? checkTitle(check);
}

export function checkExplains(check: HygieneCheck): string {
  return CHECK_VOICE[check.id]?.explains ?? check.description;
}

/** "25.00%" → "25%", "12.50%" → "12.5%". */
function tidyPercent(display: string): string {
  return display.replace(/(\.\d*?)0+%/, '$1%').replace(/\.%/, '%');
}

/** "< 35%" → "under 35%", ">= 15%" → "at least 15%". */
export function humanizeReference(display: string): string {
  // Only the numeric token survives — backend reference strings often
  // trail engineering copy ("35% of normalized portfolio value
  // (look-through)") that must never reach a tile.
  const match = /^\s*(<=|>=|<|>)\s*([\d.,]+\s*(?:%|bps|x|×|days|d)?)/.exec(
    display
  );
  if (!match) return display;
  const phrase = {
    '<': 'under',
    '<=': 'at most',
    '>': 'over',
    '>=': 'at least'
  }[match[1]! as '<' | '<=' | '>' | '>='];
  return `${phrase} ${tidyPercent(match[2]!.trim())}`;
}

/**
 * A measurement line only exists for checks with a numeric threshold —
 * qualitative checks are carried by their sentence, never by "X vs Y".
 */
export function measurementLine(check: HygieneCheck): string | null {
  if (check.current.display == null) return null;
  if (!/^\s*(<=|>=|<|>)\s*\d/.test(check.reference.display)) return null;
  return `Today: ${tidyPercent(check.current.display)} · Guideline: ${humanizeReference(check.reference.display)}`;
}

/** The valuation source `VALUATION_INCOMPLETE` is judged on. */
const VALUATION_SOURCE_ID = 'normalized-position-ledger';

/**
 * The served priced share of the position ledger, only when it is a real
 * partial worth naming: at least a whole percent priced and a whole percent
 * unpriced, so the copy never rounds to "100% have a price, so this is on
 * hold". The copy never computes a share itself.
 */
function pricedShare(
  hygiene: HygieneCompleted | undefined
): { priced: string; unpriced: string } | null {
  const bps = hygiene?.provenance.sources.find(
    (source) => source.sourceId === VALUATION_SOURCE_ID
  )?.coverageBps;
  if (bps == null || !Number.isFinite(bps) || bps < 100 || bps > 9900) {
    return null;
  }
  // One whole percent, floored, and its complement derived from it, so the
  // two figures always add to 100 (rounding each side alone can read 67%
  // priced beside 34% unpriced).
  const priced = Math.floor(bps / 100);
  return { priced: `${priced}%`, unpriced: `${100 - priced}%` };
}

/** Summary groups: several reason codes can share one clause. */
type PendingGroup =
  | 'allocation'
  | 'pricing'
  | 'allocation_records'
  | 'asset_links'
  | 'asset_classes'
  | 'exit_estimates'
  | 'feed'
  | 'stale'
  | 'account'
  | 'guideline'
  | 'unmodelled'
  | 'unknown';

interface PendingVoice {
  group: PendingGroup;
  /** The check row: why this one check is waiting, one plain sentence. */
  line: (hygiene?: HygieneCompleted) => string;
}

/**
 * Every reason code the hygiene contract documents for a check on hold,
 * in the reader's language. The backend's engine markers (`EVIDENCE_MISSING`
 * and the like) are deliberately absent: they say a check is waiting, not
 * why, so the fallback sentence carries them.
 */
const PENDING_VOICE: Record<string, PendingVoice> = {
  EMPTY_POPULATION: {
    group: 'allocation',
    line: () => 'There is nothing in this portfolio to measure yet.'
  },
  VALUATION_INCOMPLETE: {
    group: 'pricing',
    line: (hygiene) => {
      const share = pricedShare(hygiene);
      return share
        ? `${share.priced} of your holdings have a current price, so this check is on hold until the rest do.`
        : 'Some holdings have no current price, so this check is on hold until every holding has one.';
    }
  },
  ALLOCATION_PROVENANCE_MISSING: {
    group: 'allocation_records',
    line: () =>
      'Some of your value has no record of what it is invested in, so this check is on hold.'
  },
  EXPOSURE_LINKAGE_INCOMPLETE: {
    group: 'asset_links',
    line: () =>
      'Some of what you hold is not linked to a known asset yet, so this check is on hold.'
  },
  ASSET_CLASSIFICATION_INCOMPLETE: {
    group: 'asset_classes',
    line: () =>
      'Some of what you hold has no asset class yet, so this check is on hold.'
  },
  ROUTE_BOUNDS_ESTIMATE_ONLY: {
    group: 'exit_estimates',
    line: () =>
      'Exit timing for some holdings is only an estimate, so this check is on hold.'
  },
  COLLECTOR_FAILED: {
    group: 'feed',
    line: () => 'A data feed behind this check failed. We are retrying it.'
  },
  VALUATION_UNAVAILABLE: {
    group: 'feed',
    line: () =>
      'Pricing for this portfolio could not be loaded. We are retrying it.'
  },
  EXPOSURE_UNAVAILABLE: {
    group: 'feed',
    line: () =>
      'The asset breakdown for this portfolio could not be loaded. We are retrying it.'
  },
  EVIDENCE_STALE: {
    group: 'stale',
    line: () => 'The data behind this check went stale. We are refreshing it.'
  },
  ACCOUNT_KIND_UNKNOWN: {
    group: 'account',
    line: () => "We haven't identified this account's type yet."
  },
  REFERENCE_MISSING: {
    group: 'guideline',
    line: () => "The guideline for this check isn't set yet."
  },
  PROTOCOL_DIMENSION_UNMODELLED: {
    group: 'unmodelled',
    line: () =>
      'ROWA only measures one chain today, so this check cannot be scored yet.'
  },
  REFERENCE_THRESHOLD_UNDEFINED: {
    group: 'unmodelled',
    line: () =>
      'ROWA has not set a high risk limit yet, so there is nothing to score against.'
  }
};

const UNKNOWN_PENDING_LINE = "We're still connecting the data for this check.";

interface GroupVoice {
  /** Summary clause for N checks sharing this group; no closing sentence. */
  clause: (n: number, hygiene?: HygieneCompleted) => string;
  /** Tile line when every pending check in a category shares this group. */
  detail: (hygiene?: HygieneCompleted) => string;
}

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

const GROUP_VOICE: Record<PendingGroup, GroupVoice> = {
  allocation: {
    clause: (n) =>
      `${plural(n, 'check starts', 'checks start')} after your first allocation.`,
    detail: () => 'Starts after your first allocation'
  },
  pricing: {
    clause: (n, hygiene) => {
      const share = pricedShare(hygiene);
      return `${plural(n, 'check is', 'checks are')} on hold because ${share ? `${share.unpriced} of your holdings have` : 'some holdings have'} no current price.`;
    },
    detail: (hygiene) => {
      const share = pricedShare(hygiene);
      return `Waiting on prices for ${share ? `${share.unpriced} of` : 'some'} holdings`;
    }
  },
  allocation_records: {
    clause: (n) =>
      `${plural(n, 'check is', 'checks are')} on hold because some of your value has no record of what it is invested in.`,
    detail: () => 'Waiting on allocation records'
  },
  asset_links: {
    clause: (n) =>
      `${plural(n, 'check is', 'checks are')} on hold because some holdings are not linked to a known asset yet.`,
    detail: () => 'Waiting on asset links'
  },
  asset_classes: {
    clause: (n) =>
      `${plural(n, 'check is', 'checks are')} on hold because some holdings have no asset class yet.`,
    detail: () => 'Waiting on asset classes'
  },
  exit_estimates: {
    clause: (n) =>
      `${plural(n, 'check is', 'checks are')} on hold because exit timing for some holdings is only an estimate.`,
    detail: () => 'Exit timing is only an estimate'
  },
  feed: {
    clause: (n) =>
      `${plural(n, 'check is', 'checks are')} waiting on a data feed we are retrying.`,
    detail: () => 'Retrying a data feed'
  },
  stale: {
    clause: (n) =>
      `${plural(n, 'check is', 'checks are')} waiting on a data refresh.`,
    detail: () => 'Refreshing stale data'
  },
  account: {
    clause: (n) =>
      `${plural(n, 'check is', 'checks are')} waiting until we identify this account's type.`,
    detail: () => 'Waiting on account type'
  },
  guideline: {
    clause: (n) =>
      `${plural(n, 'check has', 'checks have')} no guideline set yet.`,
    detail: () => 'No guideline set yet'
  },
  unmodelled: {
    clause: (n) => `${plural(n, 'check', 'checks')} cannot be scored yet.`,
    detail: () => 'Cannot be scored yet'
  },
  unknown: {
    clause: (n) =>
      `${plural(n, 'check is', 'checks are')} waiting on data ROWA is still connecting.`,
    detail: () => 'Waiting on data, nothing to do yet'
  }
};

/**
 * The one documented reason a check is waiting: the first documented code
 * the check carries, else the provenance warning that names the check (a
 * warning with no `affectedCheckIds` is assessment-wide and names every
 * check).
 */
function pendingReasonCode(
  check: HygieneCheck,
  hygiene?: HygieneCompleted
): string | undefined {
  const fromCheck = check.reasonCodes.find((code) =>
    Object.hasOwn(PENDING_VOICE, code)
  );
  if (fromCheck) return fromCheck;
  const warnings =
    hygiene?.provenance.warnings.filter((warning) =>
      Object.hasOwn(PENDING_VOICE, warning.code)
    ) ?? [];
  // A warning that names this check beats an assessment-wide one.
  return (
    warnings.find((warning) => warning.affectedCheckIds.includes(check.id)) ??
    warnings.find((warning) => warning.affectedCheckIds.length === 0)
  )?.code;
}

function pendingGroup(
  check: HygieneCheck,
  hygiene?: HygieneCompleted
): PendingGroup {
  const code = pendingReasonCode(check, hygiene);
  return code ? PENDING_VOICE[code]!.group : 'unknown';
}

/** Why a check is waiting, in the reader's language. */
export function pendingLine(
  check: HygieneCheck,
  hygiene?: HygieneCompleted
): string {
  const code = pendingReasonCode(check, hygiene);
  return code ? PENDING_VOICE[code]!.line(hygiene) : UNKNOWN_PENDING_LINE;
}

/** Whose move is it while a check waits — ours, or the reader's first allocation. */
export function pendingOwnerLine(
  check: HygieneCheck,
  hygiene: HygieneCompleted
): string {
  return pendingGroup(check, hygiene) === 'allocation'
    ? 'Starts after your first allocation'
    : 'On ROWA, nothing for you to do';
}

/** Pending checks tallied by summary group, largest group first. */
function pendingGroups(
  checks: HygieneCheck[],
  hygiene: HygieneCompleted
): { group: PendingGroup; count: number }[] {
  const counts = new Map<PendingGroup, number>();
  for (const check of checks) {
    if (check.status !== 'not_evaluable') continue;
    const group = pendingGroup(check, hygiene);
    counts.set(group, (counts.get(group) ?? 0) + 1);
  }
  // Map preserves first appearance, so equal counts keep served order.
  return [...counts]
    .map(([group, count]) => ({ group, count }))
    .sort((left, right) => right.count - left.count);
}

const CATEGORY_EVALUATED = new Set(['met', 'partially_met', 'not_met']);

const SEVERITY_RANK: Record<string, number> = {
  critical: 0,
  attention: 1,
  standard: 2
};

/**
 * The one check that best explains a category's score right now: the worst
 * failing check (severity-ranked), else the tightest passing measurement.
 * Gives the tile a reason, not just a number.
 */
export function categoryInsight(category: HygieneCategory): string | undefined {
  const byConcern = (statuses: KnownHygieneCheckStatus[]) =>
    category.checks
      .filter((check) => (statuses as string[]).includes(check.status))
      .sort(
        (left, right) =>
          (SEVERITY_RANK[left.severity] ?? 3) -
          (SEVERITY_RANK[right.severity] ?? 3)
      )[0];

  const failing = byConcern(['not_met', 'partially_met']);
  if (failing) {
    const measurement = measurementLine(failing);
    return measurement
      ? `${checkTitle(failing)} · ${measurement}`
      : `${checkTitle(failing)} · ${checkStatusLabel(failing.status).toLowerCase()}`;
  }

  const passing = category.checks.find(
    (check) => check.status === 'met' && measurementLine(check) != null
  );
  return passing
    ? `${checkTitle(passing)} · ${measurementLine(passing)}`
    : undefined;
}

/**
 * Tile detail line: data status in one calm phrase. An unscored category
 * whose pending checks all share one served reason names that reason.
 */
export function categoryDetailLine(
  category: HygieneCategory,
  hygiene: HygieneCompleted
): string {
  const applicable = category.checks.filter(
    (check) => check.status !== 'not_applicable'
  );
  const evaluated = applicable.filter((check) =>
    CATEGORY_EVALUATED.has(check.status)
  );
  if (category.score != null) {
    return `${evaluated.length} of ${applicable.length} ${applicable.length === 1 ? 'check has' : 'checks have'} data`;
  }
  const groups = pendingGroups(applicable, hygiene);
  if (groups.length === 1) {
    return GROUP_VOICE[groups[0]!.group].detail(hygiene);
  }
  return groups.some((entry) => entry.group === 'allocation')
    ? GROUP_VOICE.allocation.detail(hygiene)
    : GROUP_VOICE.unknown.detail(hygiene);
}

/**
 * The card's note lines for a withheld score: at most two calm sentences
 * that say whose move it is. Waiting checks are tallied by their served
 * reason, largest group first. Never per-warning fragments.
 */
export function assessmentNotes(hygiene: HygieneCompleted): string[] {
  const notes: string[] = [];
  // Only an assessment-wide warning (no named checks) earns the "most
  // checks" line; one scoped to named checks joins the tally below.
  const allocationWide = hygiene.provenance.warnings.some(
    (warning) =>
      warning.code === 'EMPTY_POPULATION' &&
      warning.affectedCheckIds.length === 0
  );
  if (allocationWide) {
    notes.push('Most checks start after your first vault allocation.');
  }
  const groups = pendingGroups(
    hygiene.categories.flatMap((category) => category.checks),
    hygiene
  )
    // The allocation line above already carries those checks.
    .filter((entry) => !(allocationWide && entry.group === 'allocation'));
  if (groups.length > 0) {
    const clauses = groups.map((entry) =>
      GROUP_VOICE[entry.group].clause(entry.count, hygiene)
    );
    // "Nothing you need to do" is only true of checks waiting on us; the
    // reader's first allocation is their move.
    if (groups.some((entry) => entry.group !== 'allocation')) {
      clauses.push('Nothing you need to do.');
    }
    notes.push(clauses.join(' '));
  }
  return notes;
}

/** Problems first, then the reader's move, then what we're still wiring. */
export const DRIVER_STATE_PRIORITY: Record<string, number> = {
  'at-risk': 0,
  watch: 1,
  sound: 2,
  'awaiting-allocation': 3,
  gathering: 4,
  'not-tracked': 5
};

/** Reader voice for v2 confidence signals — `unknown` is honest absence. */
const SIGNAL_STATUS_LABEL: Record<string, string> = {
  ok: 'Healthy',
  degraded: 'Degraded',
  unknown: 'Not wired yet'
};

export function signalStatusLabel(status: string): string {
  return SIGNAL_STATUS_LABEL[status] ?? 'Waiting on data';
}

/** Signal ids reuse former check ids, so their voices apply directly. */
export function signalName(id: string): string {
  return CHECK_VOICE[id]?.title ?? id;
}

export interface CheckVisual {
  id: string;
  /** Compact name sized for the row. */
  title: string;
  /** Full reader-voice name, for tooltips and accessible labels. */
  longTitle: string;
  status: string;
  /** Tidied current reading, e.g. "100%" — only when numeric. */
  current?: string;
  /** Humanized guideline, e.g. "under 35%" — only when numeric. */
  guideline?: string;
  /**
   * The served values behind the meter, both in the same served unit. Set
   * only when the two can be compared and give the meter a scale.
   */
  currentNum?: number;
  guidelineNum?: number;
}

/** A reference that states a threshold: "< 35%", ">= 15%". */
const THRESHOLD_REFERENCE = /^\s*(<=|>=|<|>)\s*\d/;

/** A served decimal value as a number; true, false and text are not one. */
function servedNumber(
  value: string | boolean | null | undefined
): number | undefined {
  if (typeof value !== 'string' || !/^-?\d+(?:\.\d+)?$/.test(value.trim())) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * The tile's third layer: one visual per check. Quantitative checks carry
 * current-vs-guideline numbers for a bullet meter; qualitative and
 * waiting checks fall back to their status alone.
 */
export function checkVisual(check: HygieneCheck): CheckVisual {
  const visual: CheckVisual = {
    id: check.id,
    title: checkShortTitle(check),
    longTitle: checkTitle(check),
    status: check.status
  };

  // The words: today's reading beside its guideline, straight from the
  // served display strings, for any check with a stated threshold.
  if (
    check.current.display == null ||
    !THRESHOLD_REFERENCE.test(check.reference.display)
  ) {
    return visual;
  }
  visual.current = tidyPercent(check.current.display.trim());
  visual.guideline = humanizeReference(check.reference.display);

  // The arithmetic: only from the served values. `display` explains and is
  // not the source of arithmetic, so a meter is drawn only when both values
  // are numbers in the same stated unit and give it a scale. Zero against
  // zero has none, and dividing by it printed NaN% widths.
  const currentNum = servedNumber(check.current.value);
  const guidelineNum = servedNumber(check.reference.value);
  const currentUnit = check.current.unit.trim().toLowerCase();
  const referenceUnit = (check.reference.unit ?? '').trim().toLowerCase();
  if (
    currentNum != null &&
    guidelineNum != null &&
    currentNum >= 0 &&
    guidelineNum >= 0 &&
    Math.max(currentNum, guidelineNum) > 0 &&
    currentUnit !== '' &&
    currentUnit === referenceUnit
  ) {
    visual.currentNum = currentNum;
    visual.guidelineNum = guidelineNum;
  }

  return visual;
}
